import { useState, useRef, useEffect } from "react";
import { Icons, Logo } from "@/assets";
import { createRipple } from "@/utils/ripple";
import { COOKIE_NAMES } from "@/constants/auth";
import { eraseCookie, getCookie } from "@/utils/cookies";
import { Toast } from "@/utils/toast";
import { decodeJwt } from "@/utils/authUtils";
import { useGetUserProfile } from "@/hooks/auth/useGetUserDetails";

type AuthTokenPayload = {
    name?: string;
    fullName?: string;
    userName?: string;
    email?: string;
    sub?: string;
};

const normalizeKey = (value: string) => value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const isText = (value: unknown): value is string => typeof value === "string" && Boolean(value.trim());

const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const PROFILE_NAME_KEYS = new Set([
    "name",
    "fullname",
    "full_name",
    "username",
    "user_name",
    "displayname",
    "display_name",
    "firstname",
    "first_name",
]);

const PROFILE_NAME_LABELS = new Set([
    "name",
    "fullname",
    "username",
    "displayname",
    "firstname",
]);

const titleCase = (value: string): string =>
    value
        .split(/[._\-\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

const friendlyNameFromEmail = (emailOrIdentifier: string): string => {
    const localPart = emailOrIdentifier.includes("@")
        ? emailOrIdentifier.split("@")[0]
        : emailOrIdentifier;

    return localPart ? titleCase(localPart) : "";
};

const collectProfileNames = (node: unknown, output: string[] = []): string[] => {
    if (!node || typeof node !== "object") return output;

    if (Array.isArray(node)) {
        node.forEach((item) => collectProfileNames(item, output));
        return output;
    }

    const record = node as Record<string, unknown>;
    const label = [record.label, record.name, record.fieldName, record.displayName, record.title]
        .find(isText);
    const labelLooksLikeName = label ? PROFILE_NAME_LABELS.has(normalizeKey(label)) : false;

    if (labelLooksLikeName) {
        const directValue = record.value;
        if (isText(directValue)) {
            output.push(directValue.trim());
        } else if (directValue && typeof directValue === "object") {
            Object.values(directValue as Record<string, unknown>).forEach((item) => {
                if (isText(item)) {
                    output.push(item.trim());
                    return;
                }
                if (item && typeof item === "object" && isText((item as Record<string, unknown>).value)) {
                    output.push(String((item as Record<string, unknown>).value).trim());
                }
            });
        }
    }

    Object.entries(record).forEach(([key, value]) => {
        const keyName = normalizeKey(key);
        const isFieldMetadataName = keyName === "name" && Object.prototype.hasOwnProperty.call(record, "value");
        if (PROFILE_NAME_KEYS.has(keyName) && !isFieldMetadataName && isText(value)) {
            output.push(value.trim());
        }
        collectProfileNames(value, output);
    });

    return output;
};

const getProfileDisplayName = (profileInfo: Record<string, unknown> | undefined): string => {
    const [profileName] = collectProfileNames(profileInfo).filter((name) => name.length > 1 && !isEmail(name));
    return profileName ?? "";
};

const toDisplayName = (payload: AuthTokenPayload | null, profileInfo?: Record<string, unknown>): string => {
    const profileName = getProfileDisplayName(profileInfo);
    if (profileName) return profileName;

    const explicitName = payload?.name || payload?.fullName || payload?.userName;
    if (explicitName && !isEmail(explicitName)) return explicitName;

    const emailOrIdentifier =
        [payload?.email, payload?.sub, explicitName, localStorage.getItem("userEmail")]
            .find((value): value is string => isText(value)) ?? "";
    const friendlyName = friendlyNameFromEmail(emailOrIdentifier);
    return friendlyName || "User";
};

export const Topbar = () => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    const tokenPayload = decodeJwt<AuthTokenPayload>(getCookie(COOKIE_NAMES.ACCESS_TOKEN) ?? "");
    const { data: profileInfo } = useGetUserProfile();
    const user = {
        name: toDisplayName(tokenPayload, profileInfo),
        avatarUrl: `https://i.pravatar.cc/150?u=${encodeURIComponent(tokenPayload?.email || tokenPayload?.sub || "user")}`,
    };

    // Close popup on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = async (e: React.MouseEvent<HTMLDivElement>) => {
        createRipple(e);

        try {
           
            // sessionStorage.removeItem("UserName");
            // sessionStorage.removeItem("Email");
            eraseCookie(COOKIE_NAMES.ACCESS_TOKEN);

            Toast.success("Logged out successfully!");

            await new Promise(resolve => setTimeout(resolve, 500));

            window.location.replace("/login");
        } catch (error) {
            console.error("Logout failed:", error);
            Toast.error("Logout failed. Please try again.");
        }
    };

    return (
        <header className="w-full h-[72px] bg-white border-b border-[#ebebf0] flex items-center justify-between px-[30px] py-2 z-50 sticky top-0">
            {/* Left: Logo + Title */}
            <div className="flex items-center gap-3">
                <Logo.PaySftLogo />
                <span className="text-lg font-bold text-gray-900 tracking-tight">PaySFT Admin Portal</span>
            </div>

            {/* Right: Profile */}
            <div className="flex items-center gap-5">
                {/* Profile Section */}
                <div ref={profileRef} className="relative">
                    <div 
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="h-[52px] rounded-full hover:bg-gray-30 cursor-pointer transition-colors flex items-center px-2 gap-2 border border-transparent hover:border-gray-200"
                    >
                        <div className="w-[40px] h-[40px] rounded-full overflow-hidden bg-gray-300 border border-white shadow-sm">
                            <img src={user.avatarUrl} alt="User" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold text-black leading-none">{user.name}</span>
                        </div>
                        <Icons.ChevronDownIcon 
                            className={`w-4 h-4 text-brand transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} 
                        />
                    </div>

                    {/* --- Profile Popup --- */}
                    {isProfileOpen && (
                        <div className="absolute top-full right-0 mt-2 w-[400px] bg-white rounded-[24px] shadow-2xl border border-gray-100 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                            <div className="p-6 flex flex-col h-full">
                                
                                {/* Bottom Actions */}
                                <div className="mt-auto flex flex-col gap-4">
                                    {/* Logout */}
                                    <div 
                                        onClick={handleLogout}
                                        className="flex items-center gap-4 p-4 rounded-[16px] border border-gray-200 cursor-pointer hover:bg-red-50 hover:border-red-100 hover:text-red-600 transition-all group"
                                    >
                                        <div className="w-10 h-10 flex items-center justify-center">
                                            <Icons.LogoutIcon className="w-6 h-6 text-gray-900 group-hover:text-red-600" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-base font-bold text-gray-900 group-hover:text-red-600">Logout</span>
                                            <span className="text-xs text-gray-400 group-hover:text-red-400">Logout of your account</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};
